import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Play, Layers, Film, Box, Palette, Megaphone, Users } from "lucide-react";
import HeroSection from "../components/home/HeroSection";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const services = [
  { icon: Layers, title: "Motion Graphics", desc: "Animated content that brings your brand to life across every platform." },
  { icon: Film, title: "Video Editing", desc: "Polished edits with pacing, colour and sound that holds attention." },
  { icon: Box, title: "3D & Animation", desc: "Three-dimensional modelling and animation for product, brand and concept work." },
  { icon: Palette, title: "Graphic Design", desc: "Visual design for digital, social and print that reinforces your brand." },
  { icon: Megaphone, title: "Brand Content", desc: "Campaign assets, social content and brand films built for results." },
  { icon: Users, title: "Agency Support", desc: "White-label creative support. Your clients, your markup, my expertise." },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, clientRes] = await Promise.all([
          axios.get(`${API}/portfolio?featured=true`),
          axios.get(`${API}/clients`),
        ]);
        setProjects(projRes.data.slice(0, 4));
        setClients(clientRes.data.filter((c) => c.logo));
      } catch {
        // Silently handle
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F6F3]" data-testid="home-page">
      <HeroSection />

      {/* Services */}
      <section className="py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">
              § 02 / Services
            </p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C]">
              Everything you need,<br />
              <em className="not-italic text-[#FF5E3A]">done properly.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="bg-[#F8F6F3] rounded-2xl p-8 group hover:bg-[#1C1C1C] transition-colors duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF5E3A]/10 mb-6 group-hover:bg-[#FF5E3A]/20 transition-colors">
                  <service.icon size={22} className="text-[#FF5E3A]" />
                </div>
                <h3 className="text-lg font-bold text-[#1C1C1C] mb-3 group-hover:text-white transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#1C1C1C]/50 group-hover:text-white/60 transition-colors">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Work */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]" data-testid="work-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">
                § 03 / Selected Work
              </p>
              <h2 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C]">
                Recent <em className="not-italic text-[#FF5E3A]">projects.</em>
              </h2>
            </div>
            <Link to="/portfolio">
              <button className="bg-[#1C1C1C] text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#FF5E3A] transition-colors duration-300 flex items-center gap-2" data-testid="view-all-work-btn">
                View All Work <ArrowRight size={14} />
              </button>
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group cursor-pointer overflow-hidden rounded-2xl"
                onClick={() => {
                  if (project.client_slug) {
                    navigate(`/clients/${project.client_slug}`);
                  }
                }}
                data-testid={`project-tile-${project.id}`}
              >
                <div className="relative aspect-video overflow-hidden bg-[#E8E5E0]">
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FF5E3A] mb-1">
                      {project.client}
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-white">
                      {project.title}
                    </h3>
                  </div>

                  {project.video_url && project.video_type !== "image" && (
                    <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Play size={16} fill="white" stroke="white" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos */}
      {clients.length > 0 && (
        <section className="py-20 bg-white overflow-hidden" data-testid="clients-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.p
              {...fadeUp}
              className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-12"
            >
              Trusted by brands & agencies
            </motion.p>
          </div>
          <div className="relative">
            <div className="flex items-center gap-16 animate-marquee">
              {[...clients, ...clients, ...clients].map((client, index) => (
                <div
                  key={`${client.slug}-${index}`}
                  className="flex-shrink-0 cursor-pointer opacity-40 hover:opacity-100 transition-opacity duration-300"
                  onClick={() => navigate(`/clients/${client.slug}`)}
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="h-10 md:h-14 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Teaser */}
      <section className="py-24 md:py-32 bg-[#1C1C1C]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-white/30 mb-6">
                § 04 / About
              </p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white mb-8">
                One creative.<br />
                <em className="not-italic text-[#FF5E3A]">Every skill you need.</em>
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-white/60 mb-6">
                I'm Elliot — a multidisciplinary creative with years of experience working both in-house and freelance. I take projects from initial idea through to final delivery, including creative direction, design, animation, editing and delivery.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-white/60 mb-10">
                Add motion design to your agency without hiring in-house. I work invisibly behind your team at an agreed trade rate — you control the client relationship and set your own markup.
              </p>
              <Link to="/about">
                <button className="border-2 border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-[#FF5E3A] hover:border-[#FF5E3A] transition-all duration-300 flex items-center gap-2" data-testid="about-link">
                  More About Me <ArrowRight size={14} />
                </button>
              </Link>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.15 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-square overflow-hidden rounded-2xl">
                <img
                  src="https://static.wixstatic.com/media/0564fd_a9f18c99164148ba90f7fc5e72af41b0~mv2.jpg/v1/fill/w_500,h_500,fp_0.66_0.17,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/37002570_1753603594735500_2009197473574158336_n.jpg"
                  alt="Elliot travelling"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden rounded-2xl mt-8">
                <img
                  src="https://static.wixstatic.com/media/0564fd_3acb209e07e540fb8a5641fcd0d392bd~mv2.jpeg/v1/fill/w_500,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202024-09-25%20at%2016_12_51%20(1).jpeg"
                  alt="Sydney the dog"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#FF5E3A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-white/60 mb-8">
              Need an extra pair of creative hands?
            </p>
            <h2 className="text-4xl md:text-7xl font-black tracking-tight text-white mb-10">
              Let's work together.
            </h2>
            <Link to="/contact">
              <button className="bg-white text-[#1C1C1C] font-semibold text-sm px-10 py-5 rounded-full hover:bg-[#1C1C1C] hover:text-white transition-colors duration-300 inline-flex items-center gap-2" data-testid="cta-btn">
                Start a Project <ArrowRight size={14} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
